import type { ReaderFormat } from '@/components/reader';
import { useEffect, useState } from 'react';

/**
 * Which of the two formats of §9.3 the SESSION plays a page in.
 *
 * A format is not a breakpoint inside the reader - the editor switches it by
 * hand there - but the session has nobody to ask, so it reads the window:
 * `phone` under 768 px, `screen` above. The number is the one the admin shell
 * already folds its rail at (issue #24), so a person who holds a phone gets the
 * page authored for a phone.
 *
 * Watched and not read once: a rotation changes the answer, and a page that
 * kept a 640 px column on a phone turned sideways would be a page nobody can
 * read. The régime of `usePrefersReducedMotion` next door, guard on
 * `matchMedia` included - jsdom does not implement it, and a component test
 * that threw on its first line would be a test of the harness.
 */
const PHONE = '(max-width: 767px)';

function phoneQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(PHONE);
}

export function useReaderFormat(): ReaderFormat {
  const [format, setFormat] = useState<ReaderFormat>(() =>
    phoneQuery()?.matches === true ? 'phone' : 'screen',
  );

  useEffect(() => {
    const query = phoneQuery();
    if (query === null) return;
    setFormat(query.matches ? 'phone' : 'screen');
    const listen = (event: MediaQueryListEvent): void =>
      setFormat(event.matches ? 'phone' : 'screen');
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);

  return format;
}
